import React, { useState, useRef, useCallback } from 'react';
import ColumnSelector from '../shared/ColumnSelector';
import ButtonGroup from '../shared/ButtonGroup';
import { Mold, UserPreference } from '../../types/mold';
import { StatusFilterType, ViewMode } from './types';
import { SearchConfig, useFilteredMolds } from '../../hooks/useFilteredMolds';
import { usePagination } from '../../hooks/usePagination';
import { useSelection } from '../../hooks/useSelection';
import StatusFilter from './StatusFilter';
import MoldCardView from './MoldCardView';
import { getMoldTableColumns, MoldTableView } from '../MoldTableView';
import PaginationControls from './PaginationControls';
import CustomerFilter from './CustomerFilter';
import { usePreferences } from '../../context/PreferencesContext';
import { GenericColumn, getMoldColumnGroups } from '../KanbanBoard/utils/tableColumns';
import { downloadCSV } from '../../utils/exportUtils';
import MoldLabelModal from './MoldLabelModal';
import ExportPreviewModal from './ExportPreviewModal';
import { useAppState } from '../../context/AppStateContext';
import { getDefaultVisibleColumnsForTable } from '../KanbanBoard/utils/columnConfigurations';

interface MoldListSimplifiedProps {
  molds: Mold[];
  initialStatusFilters?: StatusFilterType[];
  initialCustomerFilter?: string;
  onStatusFilterChange?: (statuses: StatusFilterType[]) => void;
  onCustomerFilterChange?: (customer: string) => void;
  preferences?: UserPreference | null;
  // Search props - these will be passed from the container
  searchTerm?: string;
  selectedSearchField?: string;
}

const MoldListSimplified: React.FC<MoldListSimplifiedProps> = ({ 
  molds, 
  initialStatusFilters = ['ALL'],
  initialCustomerFilter = 'ALL',
  onStatusFilterChange,
  onCustomerFilterChange,
  preferences,
  searchTerm = '',
  selectedSearchField = 'all'
}) => {
  const [searchConfig, setSearchConfig] = useState<SearchConfig>({
    query: searchTerm,
    field: selectedSearchField,
  });
  const [statusFilter, setStatusFilter] = useState<StatusFilterType[]>(initialStatusFilters);
  const [customerFilter, setCustomerFilter] = useState(initialCustomerFilter);

  const handleStatusFilterChange = (statuses: StatusFilterType[]) => {
    setStatusFilter(statuses);
    onStatusFilterChange?.(statuses);
  };
  const handleCustomerFilterChange = (customer: string) => {
    setCustomerFilter(customer);
    onCustomerFilterChange?.(customer);
  };
  const { updatePreference } = usePreferences();
  const [viewMode, setViewMode] = useState<ViewMode>(preferences?.moldListView || 'card');
  const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState(false);
  const [filteredSortedMolds, setFilteredSortedMolds] = useState<Mold[]>([]);
  const [isMoldLabelModalOpen, setIsMoldLabelModalOpen] = useState(false);
  const [isExportPreviewOpen, setIsExportPreviewOpen] = useState(false);

  const { getMoldStatusLabel, companyCode } = useAppState();

  // Use GenericColumn<Mold>[] with company-specific configuration
  const columns: GenericColumn<Mold>[] = getMoldTableColumns({
    getStatusText: getMoldStatusLabel,
    companyCode
  });

  // Generate column groups based on available columns with company-specific configuration
  const columnGroups = getMoldColumnGroups({
    getStatusText: getMoldStatusLabel,
    companyCode
  });
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(() => {
    // Get company-specific default visible columns
    const defaultVisibleColumns: Record<string, boolean> = companyCode
      ? getDefaultVisibleColumnsForTable(companyCode, 'molds')
      : columns.reduce(
          (acc, column) => ({
            ...acc,
            [column.key]: column.key !== 'select',
          }),
          {} as Record<string, boolean>,
        );

    // Load saved user preferences and merge with defaults
    // This ensures new columns (that aren't in saved prefs) use default visibility
    const savedColumns: Record<string, boolean> = preferences?.moldTableVisibleColumns || {};
    return columns.reduce(
      (acc, column) => ({
        ...acc,
        [column.key]: savedColumns[column.key] !== undefined
          ? savedColumns[column.key]
          : defaultVisibleColumns[column.key] !== undefined
            ? defaultVisibleColumns[column.key]
            : column.key !== 'select',
      }),
      {} as Record<string, boolean>,
    );
  });

  // Separate state for CSV export columns - starts with all columns except actions/select/checkbox
  // and servicing columns (deselected by default for export)
  const [exportVisibleColumns, setExportVisibleColumns] = useState<Record<string, boolean>>(() => {
    const columnsDeselectedByDefault = [
      'servicingAlertShotThreshold',
      'servicingMaxShotThreshold',
      'shotCountDisplay',
      'cycleShotCountDisplay',
      'physicalShotCountDisplay'
    ];

    return columns.reduce(
      (acc, column) => {
        const isExcludedColumn = column.key === 'actions' || column.key === 'select' || column.key === 'checkbox';
        const isDeselectedByDefault = columnsDeselectedByDefault.includes(column.key);
        return {
          ...acc,
          [column.key]: !isExcludedColumn && !isDeselectedByDefault,
        };
      },
      {} as Record<string, boolean>,
    );
  });

  // Update search config when props change
  React.useEffect(() => {
    setSearchConfig({ query: searchTerm, field: selectedSearchField });
  }, [searchTerm, selectedSearchField]);

  // Save visible columns to preferences whenever they change
  // Use a ref to track previous values and avoid infinite loops
  const prevVisibleColumnsRef = useRef<Record<string, boolean> | null>(null);
  React.useEffect(() => {
    // Only update if values actually changed (avoid infinite loop from updatePreference changes)
    if (prevVisibleColumnsRef.current === null ||
        JSON.stringify(prevVisibleColumnsRef.current) !== JSON.stringify(visibleColumns)) {
      prevVisibleColumnsRef.current = visibleColumns;
      updatePreference({ moldTableVisibleColumns: visibleColumns });
    }
  }, [visibleColumns]);

  const filteredMolds = useFilteredMolds(molds, searchConfig, statusFilter, customerFilter);

  // Shared pagination state for both view modes
  const {
    paginatedData,
    currentPage,
    pageSize,
    totalPages,
    setPageSize,
    goToPage,
  } = usePagination({ data: filteredMolds });

  const {
    selectedIds: selectedMolds,
    toggleSelection,
    togglePage,
  } = useSelection(molds);

  const handleDataChange = useCallback((data: Mold[]) => {
    setFilteredSortedMolds(data);
  }, []);

  const handleDownloadCSV = () => {
    setIsExportPreviewOpen(true);
  };

  // Get molds to export - use selected molds if any, otherwise use all filtered sorted molds
  const moldsToExport = React.useMemo(() => {
    const source = filteredSortedMolds.length > 0
      ? filteredSortedMolds
      : filteredMolds.length > 0
        ? filteredMolds
        : molds;

    return selectedMolds.length > 0
      ? source.filter(mold => selectedMolds.includes(mold.id))
      : source;
  }, [selectedMolds, filteredSortedMolds, filteredMolds, molds]);

  const handleConfirmDownload = async () => {
    await downloadCSV(
      moldsToExport,
      exportVisibleColumns,
      columns,
      'molds.csv'
    );
  };

  const filterButtonRef = useRef<HTMLButtonElement>(null);

  const handleViewModeChange = (mode: ViewMode) => {
    setViewMode(mode);
    updatePreference({ moldListView: mode });
  };

  const handleOpenMoldLabelModal = () => {
    setIsMoldLabelModalOpen(true);
  };

  const handleCloseMoldLabelModal = () => {
    setIsMoldLabelModalOpen(false);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="px-4 sm:px-6">
          <div className="pb-0 sm:pb-1 pt-2">
            <div className="flex flex-col space-y-0 sm:space-y-1">
              <div className="flex flex-col sm:flex-row gap-1 sm:gap-2 justify-between">
                <div className='flex flex-row items-center justify-start gap-2'>
                  <StatusFilter
                    molds={molds}
                    selectedStatuses={statusFilter}
                    onStatusChange={handleStatusFilterChange}
                    isMultiSelect={true}
                  />
                  <CustomerFilter
                    molds={molds}
                    activeCustomer={customerFilter}
                    onCustomerChange={handleCustomerFilterChange}
                  />
                </div>

                <div className="flex-shrink-0">
                  <ButtonGroup
                    count={selectedMolds.length > 0 ? selectedMolds.length : null}
                    viewMode={viewMode}
                    setViewMode={handleViewModeChange}
                    onDownload={handleDownloadCSV}
                    onDownloadLabels={handleOpenMoldLabelModal}
                    setIsColumnSelectorOpen={setIsColumnSelectorOpen}
                    filterButtonRef={filterButtonRef}
                  />
                </div>
              </div>
            </div>
          </div>

          <ColumnSelector
            isOpen={isColumnSelectorOpen}
            onClose={() => setIsColumnSelectorOpen(false)}
            columns={columns}
            columnGroups={columnGroups}
            visibleColumns={visibleColumns}
            setVisibleColumns={setVisibleColumns}
          />

          <div className="mb-6">
            {viewMode === 'card' ? (
              <MoldCardView
                molds={paginatedData}
                selectedMolds={selectedMolds}
                onSelect={toggleSelection}
              />
            ) : (
              <div className="overflow-x-auto">
                <MoldTableView
                  molds={filteredMolds}
                  visibleColumns={visibleColumns}
                  selectedMolds={selectedMolds}
                  onSelect={toggleSelection}
                  onSelectAll={() => togglePage(paginatedData)}
                  pagination={true}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  onPageChange={goToPage}
                  onPageSizeChange={setPageSize}
                  onDataChange={handleDataChange}
                />
              </div>
            )}
          </div>

          {/* Only show pagination controls for card view since table has its own */}
          {viewMode === 'card' && (
            <div className="mb-20">
              <PaginationControls
                currentPage={currentPage}
                pageSize={pageSize}
                totalPages={totalPages}
                totalItems={filteredMolds.length}
                onPageChange={goToPage}
                onPageSizeChange={setPageSize}
              />
            </div>
          )}
        </div>
      </div>

      {isMoldLabelModalOpen && (
        <MoldLabelModal
          onClose={handleCloseMoldLabelModal}
          selectedMoldIds={selectedMolds.length > 0 ? selectedMolds.map((id) => Number(id)) : []}
        />
      )}

      {isExportPreviewOpen && (
        <ExportPreviewModal
          onClose={() => setIsExportPreviewOpen(false)}
          onDownload={handleConfirmDownload}
          dataCount={moldsToExport.length}
          columns={columns}
          columnGroups={columnGroups}
          tableVisibleColumns={visibleColumns}
          visibleColumns={exportVisibleColumns}
          setVisibleColumns={setExportVisibleColumns}
          filename="molds.csv"
        />
      )}
    </div>
  );
};

export default MoldListSimplified;
